import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Route } from 'react-router-dom'
import { ModalControl } from '@entrata/portals-common/lib/components'
import { ArrowLeftLine, StackedDots } from '@entrata/portals-common/lib/icons'

import styled from '@emotion/styled'
import { css } from '@emotion/core'

import { underlineFocus, FocusSpan } from './focus-styles'
import { getEmbeddedViewFlag } from 'portal-modules/common/utils/get-embedded-flag'

const btnBase = ({ theme }) => css`
  background: none;
  border: none;
  padding: 0;
  color: inherit;
`

const StyledH2 = styled('h2')`
  display: flex;
  font-size: 16px;
  justify-content: space-between;
  text-transform: none;
  color: ${({ theme }) => theme.text.sidebar_foreground};

  @media screen and (min-width: 768px) {
    font-size: 21px;
  }
`

const StyledBack = styled('button')`
  ${btnBase}
  margin-right: 16px;
  vertical-align: middle;
  ${underlineFocus};
`

const StyledBtn = styled('button')`
  ${btnBase}
  margin-left: 16px;
  ${underlineFocus};
`

const props = {
  children: PropTypes.any,
  /** Optional class for styling the header */
  className: PropTypes.string,
  /** an id for the header element. */
  id: PropTypes.string,
  /** Optional function for the back button (instead of using `returnTo`). The back button will show if this or `returnTo` is passed. */
  onBackClick: PropTypes.func,
  /** Function to render a settings modal. If passed, shows the settings icon (three dots) in the far right. */
  renderModal: PropTypes.func,
  hideFilter: PropTypes.bool,
}

const Header = ({
  children,
  id,
  className,
  onBackClick,
  renderModal,
  hideFilter,
}) => (
  <StyledH2 id={id} className={className}>
    <span>
      {typeof onBackClick === 'function' && (
        <StyledBack onClick={onBackClick}>
          <FocusSpan>
            <ArrowLeftLine size={16} />
          </FocusSpan>
        </StyledBack>
      )}
      {children}
    </span>
    {typeof renderModal === 'function' && !hideFilter && (
      <ModalControl>
        {({ onClick, ref, ...rest }) => (
          <Fragment>
            <StyledBtn onClick={onClick} ref={ref}>
              <FocusSpan>
                <StackedDots size={14} />
              </FocusSpan>
            </StyledBtn>
            {renderModal(rest)}
          </Fragment>
        )}
      </ModalControl>
    )}
  </StyledH2>
)

Header.propTypes = props

const ContentHeader = ({ returnTo, ...props }) =>
  returnTo ? (
    <Route
      render={({ history }) => (
        <Header
          {...props}
          onBackClick={() => history.push(returnTo, { reverse: true })}
        />
      )}
    />
  ) : (
    <Header {...props} />
  )

ContentHeader.propTypes = {
  ...props,
  /** Path to return to. The back button will show if this or `onBackClick` is passed. */
  returnTo: PropTypes.string,
}

export default ContentHeader
